/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a collapsed row says it does** — one summary, for every surface that collapses one.
 *
 * There were two: this, and `ResponseMappingEditor`'s private `sourceSummary`, which returned
 * `s.type` for anything that was not `static` or `calculated` — so a conditional row read literally
 * **`conditional`** and a calculated one **`computed`**. That is *one name per concept* inside one
 * console and `#1045`'s *naming a mechanism is not describing an effect* in the same line, and the
 * shared one was not blameless either: a calculation read *"add with 3 operands"*, which is the
 * mechanism (`#1269`'s second implementation).
 *
 * Three rules the summary follows, each of which is a rule this repo already has:
 *
 * - **It states the outcome, not the shape.** A calculation reads as the arithmetic an operator
 *   wrote — `pod.remainingDuration − (response.bid × 0.7)` — because that is what the row does, and
 *   because after `E3` the shape it has is a rendering decision rather than something the author
 *   picked (`X39`).
 * - **It does not state the default, and that is a correction rather than an omission** (`#1269`,
 *   2026-09-09). The first cut appended it here, and Todd's screenshot is the argument: the row
 *   already renders `→` for *resolves to* (`→ omitted`, `→ (empty)`), so a second meaning for one
 *   glyph turned `pod.roundFloorCpm → "6" + demandSource.weight → "2" → 7` into a chain nobody can
 *   read. `#1045`'s *never reuse a name the UI already gives to something else*, arriving on a
 *   symbol. The row has a **right-aligned `⤵` mark** for this, built for `#913` with its own
 *   sacrifice order — see `MappingRow.marks.default`.
 * - **It never claims more than it can show.** A predicate the flat builder cannot render says so
 *   rather than naming its first condition, for the same reason `flattenPredicate` returns `null`.
 */

import {
  readComputedOperands,
  readConditionOperands,
  operationArity,
  type ComputedExpression,
  type ComputedOperation,
  type CriteriaNode,
  type MacroCondition,
  type MacroExpression,
  type ComputedOperand,
} from '../../../types/decorator-expression';
import { flattenPredicate } from './condition-builder.model';
import { joinAtEachPosition } from '@own-path';

/**
 * **A literal, quoted only where the quotes say something.**
 *
 * The quotes exist for one reason: to separate a literal from a path, since both are just text in
 * this read-out. **A number cannot read as a path** — `readsAsPath` requires a declared root, so
 * `3` and `1.5` are literals under every reading — which means quoting one adds no information and
 * takes some away: reported by Todd against `(pod.curValue − pod.maxDuration) × "3"`, where the
 * quotes claim *text* on an operand the multiply resolves with `Number()`.
 *
 * An **empty** literal stays quoted, because there the quotes are the whole message: `""` is a
 * declared empty value, and it is exactly the case that is otherwise indistinguishable from absence.
 */
function asLiteral(value: string): string {
  const trimmed = value.trim();
  // `Number('')` is `0`, so the empty case is excluded before the parse rather than by it — the
  // same trap `NumberField` exists for.
  return trimmed !== '' && Number.isFinite(Number(trimmed)) ? value : `"${value}"`;
}

/** How an operation reads between its operands — the same vocabulary the editor renders. */
const OPERATION_READS: Record<ComputedOperation, string> = {
  concat: '+',
  add: '+',
  subtract: '−',
  multiply: '×',
  divide: '÷',
  avg: 'and',
  timestamp: 'timestamp',
  rnd: 'random number',
  uuid: 'uuid',
};

/**
 * A calculation, as the arithmetic it is.
 *
 * **Parenthesised only where it is a group**, which is what makes the reading honest: `X43` is that
 * operands apply left to right with **no precedence**, so `a − b × c` means `(a − b) × c` and the
 * summary must not imply otherwise. A group is the only thing that reassociates, and it is the only
 * thing that gets brackets.
 */
function summarizeComputed(
  expression: ComputedExpression,
  nameFor?: ExpressionNameLookup,
  testedFor?: TestedLabelLookup,
): string {
  const operation = expression.operation ?? 'concat';
  if (operationArity(operation) === 'produces') return OPERATION_READS[operation];

  const operands = readComputedOperands(expression);
  if (operands.length === 0) return '(no operands)';
  const parts = operands.map((operand) => summarizeOperand(operand, nameFor, testedFor));
  // An average has no symbol that sits between its terms, so it reads as the function it is.
  if (operation === 'avg') return `avg(${parts.join(', ')})`;
  return parts.join(` ${OPERATION_READS[operation]} `);
}

/** One operand, with a group in brackets — see {@link summarizeComputed}. */
function summarizeOperand(
  operand: ComputedOperand,
  nameFor?: ExpressionNameLookup,
  testedFor?: TestedLabelLookup,
): string {
  // Each member of a list reads as the path it is authored as — `pod.cachedBidRefs[*].bid`.
  if (operand.type === 'collection') return joinAtEachPosition(operand);
  if (operand.type === 'computed') {
    return operationArity(operand.operation ?? 'concat') === 'produces'
      ? summarizeComputed(operand, nameFor, testedFor)
      : `(${summarizeComputed(operand, nameFor, testedFor)})`;
  }
  return summarizeMacroExpression(operand, nameFor, testedFor);
}

/**
 * **How a shared expression's name reaches a pure summariser** (`XS5`, `#1066`, Todd 2026-09-09:
 * *"Should the `shared expression` pill be showing the name of the expression instead?"* — yes).
 *
 * The name lives in the library and this is a function, not a component, so it is handed in. It is
 * **optional** and stays so: absence yields *shared expression*, which is correct and says nothing
 * untrue, so a caller that does not have the library renders an honest generic label rather than
 * nothing. That is the case the required-and-nullable rule exempts — *a prop whose absence means
 * use the correct default*.
 *
 * A surface holds the library once, above its rows, and passes this in; the alternative — a module
 * that caches names for a pure function to read — would make the summary depend on load order and
 * on whatever some other page had fetched.
 */
export interface SharedExpressionEntry {
  name: string;
  /** The expression itself, so a row can read the default in force without a second lookup. */
  expression?: MacroExpression;
  /**
   * **The entry's own version** — `entryVersion`'s answer, so a row can tell whether it is behind
   * without a second lookup and without a second way of deciding what *version* means (`#1302`).
   */
  version?: string;
}

export type ExpressionNameLookup = (ref: string) => SharedExpressionEntry | undefined;

/**
 * **Which case a tested value was tested in** (`X55b`), for a summariser that is pure and cannot see the
 * ladder. Optional for `nameFor`'s reason: absence yields *tested value*, which is true, and a surface
 * inside a ladder passes its scope so an equation reads *tested in case 1 × 2* rather than naming
 * nothing (Todd, rehearsing `S3b`: *"it's hard to tell what's actually getting multiplied"*).
 */
export type TestedLabelLookup = (name: string) => string | undefined;

export function summarizeMacroExpression(
  expression?: MacroExpression,
  nameFor?: ExpressionNameLookup,
  testedFor?: TestedLabelLookup,
): string {
  if (!expression) return '';

  if (expression.type === 'calculated') {
    const path = expression.path || '(unset path)';
    return expression.regex ? `${path} /regex/` : path;
  }

  if (expression.type === 'static') {
    if (expression.value === null || expression.value === undefined || expression.value === '') {
      return '(empty static value)';
    }
    return asLiteral(String(expression.value));
  }

  if (expression.type === 'computed') {
    return summarizeComputed(expression, nameFor, testedFor);
  }

  if (expression.type === 'conditional') {
    return summarizeCondition(expression.condition);
  }

  if (expression.type === 'suppress') {
    return 'suppress';
  }

  /**
   * **An address, summarised without its name** (`XS5`, `#1066`).
   *
   * This is a pure function over one expression and the name lives in the library, so naming it here
   * would mean threading a lookup through **seven** call sites — the optional-prop shape `#1134` is
   * about, where six of them quietly render nothing.
   *
   * So the surface that can name it does: `ValueLine` renders the read-out with *from &lt;name&gt;*
   * and the referenced expression's own summary, resolved from the library it already reads. This
   * string is what the remaining callers show, and it states what the row does rather than implying
   * a name it does not have.
   */
  // Which case tested it is the read-out's to say, where the ladder is known (`X55b`); this is pure.
  if (expression.type === 'bound') {
    const where = testedFor?.(expression.name);
    return where ? `tested in ${where}` : 'tested value';
  }

  if (expression.type === 'reference') {
    const entry = nameFor?.(expression.ref);
    return entry ? `shared: ${entry.name}` : 'shared expression';
  }

  return '';
}

/**
 * One condition, as the row summary reads it.
 *
 * **The left side is read through `readConditionOperands`**, not off `condition.path`. That field is
 * the pre-`E2b` spelling, read and never written — so summarising from it made every condition an
 * operator authored after `E2b` read as a bare operator with no subject. *A rule attached to one
 * path lapses when another is added*, arriving in a summary.
 */
function summarizeLeaf(condition: MacroCondition): string {
  // The **operator** comes off the reader too (`#1287`): read off the field, a stored `isOneOf`
  // summarised under a name the editor no longer offers, which is the same defect one door along.
  const { operator, left } = readConditionOperands(condition);
  const subject = left ? summarizeMacroExpression(left) : '';
  return `${operator}${subject ? ` on ${subject}` : ''}`;
}

/**
 * Summarise the predicate of a conditional expression (`X2`, `XS1`).
 *
 * A one-condition predicate reads exactly as it did before the swap, so no row summary changed for
 * an expression nobody has re-authored. A list says how many and how they combine, and a shape the
 * builder cannot show says **that** rather than naming the first condition and implying it is the
 * whole rule — the same reason `flattenPredicate` returns `null`.
 */
function summarizeCondition(predicate: CriteriaNode | MacroCondition): string {
  const list = flattenPredicate(predicate);
  if (!list) return 'grouped condition';
  if (list.conditions.length === 0) return 'always';
  if (list.conditions.length === 1) return summarizeLeaf(list.conditions[0]);
  return `${list.mode} of ${list.conditions.length} conditions`;
}
